import { useContext, useRef, useState } from "react";
import Chat from "@medoma/components/Chat/Chat";
import { Loading } from "@medoma/components/Loading/Loading";
import ChatContext from "@medoma/chat/ChatContext";
import {
  DISPLAYED_EVENT_TYPES,
  formatTimestamp,
  makeMessages,
  useMatrixTimelineEvents,
} from "@medoma/chat/useMatrixEvents";
import { useMatrixRoom } from "@medoma/chat/useMatrixRoom";
import { useParams } from "react-router";
import { z } from "zod";
import { usePatient } from "@/api/Patients";
import { sendTextMessage } from "@medoma/chat/matrixClient";
import { sendReadReceiptOnDemand } from "@medoma/chat/useSendReadReceipt";
import { useChatScroll } from "@medoma/chat/useChatScroll";
import { getHistory } from "@medoma/chat/getHistory";
import { SyncState } from "matrix-js-sdk";
import { useMatrixConnectionStatus } from "@medoma/chat/useMatrixConnectionStatus";
import {
  lastEventReadByUser,
  useLastReadEventPerUser,
} from "@medoma/chat/useLastReadEvent";
import { useCurrentEmployee } from "@/api/Employee";
import { employeeName, medicalTitle } from "@medoma/models/shifts";
import { useOtherUserIsTyping } from "@medoma/chat/useOtherUserIsTyping";
import { getPatientRoomAlias } from "@medoma/chat/roomAlias";
import { useUserProfile } from "@/api/Users";
import { getPatientFullName } from "@medoma/models/patients";
import { useLingui } from "@lingui/react/macro";

const ChatComponent = () => {
  const { t } = useLingui();
  const [pendingMessage, setPendingMessage] = useState("");

  const { matrixClient } = useContext(ChatContext);
  const { countryCode } = useUserProfile();

  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const roomAlias = getPatientRoomAlias({ patientId, matrixClient });
  const patientMatrixUser = `@${patientId}:${matrixClient?.getDomain()}`;
  const chatConnection = useMatrixConnectionStatus(matrixClient);
  const { roomId, getRoom } = useMatrixRoom(matrixClient, roomAlias);
  const matrixUserId = matrixClient?.getUserId();
  const { data: employee } = useCurrentEmployee();
  const { otherUserIsTyping: patientIsTyping } = useOtherUserIsTyping({
    matrixClient,
  });

  const timelineEvents = useMatrixTimelineEvents(matrixClient, roomId);

  const messages = matrixUserId
    ? makeMessages(timelineEvents, matrixUserId)
    : [];
  const latestMessage = messages[messages.length - 1]?.id ?? null;

  const messagePanelRef = useRef<HTMLDivElement>(null);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const { scrollMessagePanelToBottom } = useChatScroll({
    messagePanelRef,
    inputTextAreaRef,
    messages,
  });

  const lastEventReadByPatient = lastEventReadByUser(
    useLastReadEventPerUser({ getRoom }),
    patientMatrixUser,
  );

  const { data: patient } = usePatient(patientId);

  const shouldRenderInfoPanel =
    patientIsTyping || chatConnection === SyncState.Error;

  const lastReadEventReadByCenterUser = lastEventReadByUser(
    useLastReadEventPerUser({ getRoom }),
    matrixUserId ?? "",
  );

  // Empty room (no message) scenario is a -1 to -1 comparison, which happens to work well.
  const lastEventReadByCenterUserIsBeforeLastEventSentByNonCenterUser =
    timelineEvents.findIndex(
      ({ event: { event_id } }) =>
        event_id === lastReadEventReadByCenterUser?.eventId,
    ) <
    // Instead of `findLastIndex`, which cannot be used due to modern browser requirement
    timelineEvents.length -
      1 -
      [...timelineEvents].reverse().findIndex(
        (event) =>
          event.getSender() !== matrixUserId &&
          // Only compare with the event types that we show
          // Otherwise there might be an unshown event sent later, causing the comparison to fail
          // E.g. when clicking "show history" in an empty chat room
          DISPLAYED_EVENT_TYPES.some(
            (displayedEventType) => event.getType() === displayedEventType,
          ),
      );

  const shouldShowMarkAsReadButton =
    lastEventReadByCenterUserIsBeforeLastEventSentByNonCenterUser;

  const room = getRoom();
  if (!matrixClient || !room) {
    return <Loading message={t`Laddar chatt`} padding={24} />;
  }
  const patientName = patient ? getPatientFullName(patient.name) : t`Patienten`;
  return (
    <Chat>
      <Chat.MessagePanel
        getHistory={() => getHistory(matrixClient, room)}
        ref={messagePanelRef}
      >
        {messages.map((message) =>
          message.sender.isMe ? (
            <Chat.MessageGroup
              key={message.id}
              sender="center-user"
              status={message.status}
              timestamp={formatTimestamp(message.epochTimestamp)}
              senderName={
                message.sender.name ??
                t({ message: `Medarbetare`, context: "singular" })
              }
              senderTitle={
                medicalTitle(message.sender.title, { format: "short" }) ?? null
              }
              readIndicator={
                message.id === lastEventReadByPatient?.eventId
                  ? lastEventReadByPatient?.timestamp
                  : undefined
              }
              isLatestMessage={latestMessage === message.id}
            >
              <Chat.Message
                type="sent-by-me"
                message={message.text}
                enableClickableLinks={true}
              />
            </Chat.MessageGroup>
          ) : (
            <Chat.MessageGroup
              key={message.id}
              sender="patient"
              senderName={
                patient ? getPatientFullName(patient.name) : t`Patient`
              }
              status={message.status}
              timestamp={formatTimestamp(message.epochTimestamp)}
            >
              <Chat.Message
                type="received"
                message={message.text}
                enableClickableLinks={false}
              />
            </Chat.MessageGroup>
          ),
        )}
      </Chat.MessagePanel>
      {shouldShowMarkAsReadButton ? (
        <Chat.InfoPanel type="info">
          <Chat.MarkAsReadButton
            markAsRead={() =>
              sendReadReceiptOnDemand(
                matrixClient,
                timelineEvents,
                matrixUserId,
              )
            }
          />
        </Chat.InfoPanel>
      ) : null}
      {shouldRenderInfoPanel ? (
        <Chat.InfoPanel
          type={chatConnection === SyncState.Error ? "error" : "info"}
        >
          {chatConnection === SyncState.Error ? (
            t`Chatten fungerar inte just nu`
          ) : (
            <Chat.IsTyping text={t`${patientName} skriver`} />
          )}
        </Chat.InfoPanel>
      ) : (
        <></>
      )}
      <Chat.InputPanel>
        <Chat.InputTextArea
          inputTextAreaRef={inputTextAreaRef}
          value={pendingMessage}
          onChange={(e) => {
            setPendingMessage(e.currentTarget.value);
          }}
          onFocus={scrollMessagePanelToBottom}
        />
        <Chat.SendMessageButton
          aria-label={t`Skicka`}
          disabled={pendingMessage.trim() === ""}
          onClick={() => {
            sendTextMessage(
              matrixClient,
              room.roomId,
              pendingMessage,
              () => setPendingMessage(""),
              employee
                ? `${employeeName(employee, { countryCode, viewer: "patient" })}`
                : undefined,
              employee?.imageURL ?? null,
              employee?.competence ?? undefined,
            );
            sendReadReceiptOnDemand(matrixClient, timelineEvents, matrixUserId);
          }}
        />
      </Chat.InputPanel>
    </Chat>
  );
};

export default ChatComponent;
